// src/components/video/storyboards/tellingTime.js
//
// Storyboard for the "Telling Time" song (tellingtime2025song.mp3).
//
// Each shot names the lyric line it belongs to by its opening words. The
// times come from the song's sing-along timings (lyricSync), so if the song
// is re-synced the video follows automatically.
//
// Shot fields
//   line            opening words of the lyric line (case/punctuation ignored)
//   id              shots with the same id on consecutive lines run as one scene
//   minOccurrence   only from the Nth time this line is sung (e.g. last chorus)
//   clock           { hour, minute } to move to, or { spin: seconds per turn }
//   notchCount      tick the minute hand 1…5 notches across the line
//   fx              glowHour, glowMinute, numbers:[…], ringHours, ringMinutes,
//                   fiveCount (reveal 5,10,15… labels), fiveLabels, wobbleHour
//   labels          [{ text, color: "hour"|"minute", target, side }]
//                   target: hourHand | minuteHand | numbers | notches
//   readout         { sub, accent: "hour"|"minute" } — the digital time pill
//   cast            { marcus|emma|lily|noah: { action, dir } }
//                   action: idle wave point walk jump cheer think talk sing
//                   dir "toward" = toward the clock
//   banner          a big caption at the top

const point = { action: "point", dir: "toward" };

export const tellingTime = {
  title: "Telling Time",
  // Topic Library slugs this storyboard belongs to. The panel also matches a
  // topic titled "Telling Time", and you can pick a different topic there.
  topics: ["tellingtime", "telling-time"],
  // Bump when the storyboard changes, so saved videos show "out of date".
  version: 1,

  intro: {
    walkIn: true,
    clock: { hour: 12, minute: 0 },
    cast: {
      marcus: { action: "wave" },
      emma: { action: "wave" },
      lily: { action: "wave" },
      noah: { action: "wave" },
    },
  },

  shots: [
    // ---------- last chorus: party ----------
    {
      line: "We can tell time",
      minOccurrence: 3,
      id: "finale",
      clock: { spin: 2.5 },
      cast: {
        marcus: { action: "jump" },
        emma: { action: "cheer" },
        lily: { action: "cheer" },
        noah: { action: "jump" },
      },
    },
    // ---------- chorus ----------
    {
      line: "We can tell time",
      id: "chorus",
      clock: { spin: 4 },
      cast: {
        marcus: { action: "sing" },
        emma: { action: "sing" },
        lily: { action: "sing" },
        noah: { action: "sing" },
      },
    },

    // ---------- verse: hours ----------
    {
      line: "Now, all clocks are broken down",
      id: "broken",
      clock: { hour: 10, minute: 10 },
      fx: { ringHours: 1, ringMinutes: 1 },
      cast: { marcus: point, lily: { action: "think" } },
    },
    {
      line: "into minutes and hours",
      id: "minutes-hours",
      clock: { hour: 10, minute: 10 },
      fx: { ringHours: 1, ringMinutes: 1 },
      labels: [
        { text: "minutes", color: "minute", target: "notches", side: "right" },
        { text: "hours", color: "hour", target: "numbers", side: "left" },
      ],
      cast: { emma: point, lily: point },
    },
    {
      line: "The little hand is the hour",
      id: "little-hand",
      clock: { hour: 10, minute: 10 },
      fx: { glowHour: 1 },
      labels: [{ text: "little hand = HOUR", color: "hour", target: "hourHand", side: "left" }],
      cast: { emma: point },
    },
    {
      line: "it tells us what the hour is",
      id: "what-hour",
      clock: { hour: 10, minute: 10 },
      fx: { glowHour: 1, wobbleHour: 1 },
      labels: [{ text: "little hand = HOUR", color: "hour", target: "hourHand", side: "left" }],
      cast: { emma: point, noah: { action: "think" } },
    },
    {
      line: "When the little hand hits one",
      id: "h1",
      clock: { hour: 1, minute: 0 },
      fx: { glowHour: 1, numbers: [1] },
      readout: { sub: "1 o'clock", accent: "hour" },
      cast: { marcus: point, emma: { action: "talk" } },
    },
    {
      line: "When the little hand hits two",
      id: "h2",
      clock: { hour: 2, minute: 0 },
      fx: { glowHour: 1, numbers: [2] },
      readout: { sub: "2 o'clock", accent: "hour" },
      cast: { emma: point, marcus: { action: "cheer" } },
    },
    {
      line: "When the little hand hits three",
      id: "h3",
      clock: { hour: 3, minute: 0 },
      fx: { glowHour: 1, numbers: [3] },
      readout: { sub: "3 o'clock", accent: "hour" },
      cast: { lily: point, noah: { action: "talk" } },
    },
    {
      line: "When the little hand hits four",
      id: "h4",
      clock: { hour: 4, minute: 0 },
      fx: { glowHour: 1, numbers: [4] },
      readout: { sub: "4 o'clock", accent: "hour" },
      cast: { noah: point, lily: { action: "cheer" } },
    },

    // ---------- verse: minutes ----------
    {
      line: "Now, the big hand tells us the minutes",
      id: "big-hand",
      clock: { hour: 4, minute: 0 },
      fx: { glowMinute: 1 },
      labels: [{ text: "big hand = MINUTES", color: "minute", target: "minuteHand", side: "right" }],
      cast: { lily: point, marcus: { action: "think" } },
    },
    {
      line: "Each notch is one minute",
      id: "notches",
      clock: { hour: 4, minute: 0 },
      notchCount: true,
      fx: { glowMinute: 1 },
      readout: { sub: "{minutes}", accent: "minute" },
      cast: { noah: point, emma: { action: "talk" } },
    },
    {
      line: "Each number is five minutes",
      id: "fives",
      clock: { hour: 4, minute: 5 },
      fx: { fiveCount: true },
      cast: { marcus: point, noah: { action: "cheer" } },
    },
    {
      line: "When the big hand hits one",
      id: "m5",
      clock: { hour: 4, minute: 5 },
      fx: { glowMinute: 1, numbers: [1], fiveLabels: 12 },
      readout: { sub: "5 minutes", accent: "minute" },
      cast: { marcus: point, emma: { action: "talk" } },
    },
    {
      line: "When the big hand hits two",
      id: "m10",
      clock: { hour: 4, minute: 10 },
      fx: { glowMinute: 1, numbers: [2], fiveLabels: 12 },
      readout: { sub: "10 minutes", accent: "minute" },
      cast: { emma: point, lily: { action: "talk" } },
    },
    {
      line: "When the big hand hits three",
      id: "m15",
      clock: { hour: 4, minute: 15 },
      fx: { glowMinute: 1, numbers: [3], fiveLabels: 12 },
      readout: { sub: "15 minutes", accent: "minute" },
      cast: { lily: point, noah: { action: "talk" } },
    },
    {
      line: "When the big hand hits four",
      id: "m20",
      clock: { hour: 4, minute: 20 },
      fx: { glowMinute: 1, numbers: [4], fiveLabels: 12 },
      readout: { sub: "20 minutes", accent: "minute" },
      cast: { noah: point, marcus: { action: "cheer" } },
    },
  ],

  outro: {
    after: 6, // seconds after the last line starts
    banner: "Great job telling time!",
    cast: {
      marcus: { action: "cheer" },
      emma: { action: "wave" },
      lily: { action: "wave" },
      noah: { action: "cheer" },
    },
  },
};

export default tellingTime;
